import {Component, forwardRef, Input, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  NgbDate,
  NgbDatepicker,
  NgbDateStruct,
  NgbTimeStruct
} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from '../abstract.component';
import {required} from '@utility';

@Component({
  selector: 'app-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DateTimeInputComponent)
    },
  ],
})
export class DateTimeInputComponent extends AbstractComponent implements OnInit, ControlValueAccessor {

  public static readonly PAGE_SIZE = 15;
  currentDate: Date = new Date();

  @Input() displayTime: boolean = false;
  @Input() label: string = '';
  @Input() maxDate: Date | undefined;
  @Input() minDate: Date | undefined;
  @Input() hideErrorMessage: boolean = false;
  @Input() getErrorMessages?: (errorType: string, key: string) => string;
  @Input() formControlName?: string;
  @Input() openDropdownUpwards: boolean = false;


  @ViewChild('calendar') calendarInstance?: NgbDatepicker;
  CalendarState = CalendarState;
  calendarState: CalendarState = CalendarState.DAY;

  months: NavigationObject[] = [];
  years: NavigationObject [] = [];

  dropdownOpen: boolean = false;
  navigationDate: NgbDate = this.getNgbDate(this.currentDate);


  inputForm: FormGroup = new FormGroup<any>({
    date: new FormControl(),
    time: new FormControl(),
  });


  constructor(private controlContainer: ControlContainer, public translocoService: TranslocoService) {
    super();
  }

  ngOnInit() {
    this.markDisabledDates();
    this.initMonths();
    if (this.control.hasValidator(required)) {
      this.inputForm.controls['date'].addValidators([required]);
      this.inputForm.controls['date'].updateValueAndValidity();
    }
    if (this.displayTime) {
      this.inputForm.controls['time'].updateValueAndValidity();
    }
    this.subscriptions.push(this.inputForm.controls['date'].valueChanges.subscribe(_ => {
      const date = this.inputForm.controls['date'].value;
      const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
      if (!time && date) {
        this.inputForm.controls['time'].setValue({
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: 0,
        })
      } else {
        this.onChange(this.getTime(date, time));
      }
    }));
    this.subscriptions.push(this.inputForm.controls['time'].valueChanges.subscribe(_ => {
      const date = this.inputForm.controls['date'].value;
      const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
      if (date && time) {
        this.onChange(this.getTime(date, time));
      }
    }));
  }


  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['maxDate']?.currentValue !== changes['maxDate']?.previousValue ||
      changes['minDate']?.currentValue !== changes['minDate']?.previousValue ||
      changes['startDateTime']?.currentValue !== changes['startDateTime']?.previousValue
    ) {
      this.markDisabledDates();
    }
  }

  changeCalendarState(calendarState: CalendarState) {
    if (this.calendarState === calendarState) {
      this.calendarState = CalendarState.DAY
    } else {
      this.calendarState = calendarState;
      if (calendarState === CalendarState.YEAR) {
        this.initYears();
      }
      if (calendarState === CalendarState.MONTH) {
        this.updateMonths();
      }
    }
  }

  initMonths() {
    this.months = [];
    for (let i = 1; i <= 12; i++) {
      this.months.push(new NavigationObject(i, false))
    }
  }

  updateMonths() {
    const maxDate = this.maxCalendarDate;
    const minDate = this.minCalendarDate;
    for (let month of this.months) {
      month.disabled = false;
      if (this.navigationDate.year === maxDate.year) {
        if (month.value > maxDate.month) {
          month.disabled = true;
        }
      }
      if (this.navigationDate.year === minDate.year) {
        if (month.value < minDate.month) {
          month.disabled = true;
        }
      }
    }
  }

  initYears() {
    let from = this.navigationDate.year - 7;
    let to = this.navigationDate.year + 7;
    this.years = [];
    for (let i = from; i <= to; i++) {
      this.years.push(new NavigationObject(i, i < this.minCalendarDate.year || i > this.maxCalendarDate.year));
    }
  }


  yearSelected(event: Event, year: number) {
    event.preventDefault();
    event.stopPropagation()
    this.navigationDate.year = year
    this.calendarState = CalendarState.DAY
    this.calendarInstance?.navigateTo(this.navigationDate);
  }

  monthSelected(event: Event, month: number) {
    event.preventDefault();
    event.stopPropagation()
    this.navigationDate.month = month
    this.calendarState = CalendarState.DAY
    this.calendarInstance?.navigateTo(this.navigationDate);
  }


  navigateNext() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToNextMonth();
        break;
      case CalendarState.YEAR:
        this.navigateNextYears();
        break;
    }
  }

  navigateNextYears(): void {
    if (this.years.at(this.years.length - 1)?.value && this.years.at(this.years.length - 1)!.value < this.maxCalendarDate.year) {
      let from = this.years.at(this.years.length - 1)!.value;
      let to = from + DateTimeInputComponent.PAGE_SIZE
      this.years = [];
      for (let i = from + 1; i <= to; i++) {
        this.years.push(new NavigationObject(i, i > this.maxCalendarDate.year));
      }
    }
  }

  navigatePrevious() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToPreviousMonth();
        break;
      case CalendarState.YEAR:
        this.navigatePreviousYears();
        break;
    }
  }

  navigatePreviousYears(): void {
    if (this.years.at(0)?.value && this.years.at(0)!.value > this.minCalendarDate.year) {
      let to = this.years.at(0)!.value;
      let from = to - DateTimeInputComponent.PAGE_SIZE
      this.years = [];
      for (let i = from; i < to; i++) {
        this.years.push(new NavigationObject(i, i < this.minCalendarDate.year));
      }
    }
  }

  navigateToPreviousMonth() {
    if (this.calendarInstance?.calendar) {
      const previous: NgbDate = this.calendarInstance.calendar.getPrev(this.navigationDate, 'm');
      if (previous.year > this.minCalendarDate.year || (previous.year == this.minCalendarDate.year && previous.month >= this.minCalendarDate.month)) {
        this.navigationDate = previous;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }

  navigateToNextMonth() {
    if (this.calendarInstance?.calendar) {
      const next: NgbDate = this.calendarInstance.calendar.getNext(this.navigationDate, 'm');
      if (next.year < this.maxCalendarDate.year || (next.year == this.maxCalendarDate.year && next.month <= this.maxCalendarDate.month)) {
        this.navigationDate = next;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }


  onTouched: Function = () => {
  };
  onChange: Function = (value: Date | undefined) => {
  };

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: Function) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.inputForm.disable();
    } else {
      this.inputForm.enable();
    }
  }

  get control(): FormControl {
    return <FormControl<any>>this.controlContainer!.control!.get(this.formControlName!);
  }

  writeValue(value: any) {
    if (value) {
      let date: Date = new Date(value)
      if (date) {
        let time: NgbTimeStruct = {
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: 0,
        }
        this.inputForm.controls["time"].setValue(time);
        date.setHours(0, 0, 0, 0);
        this.inputForm.controls["date"].setValue(date);
      }
    }
  }

  markDisabled = (date: NgbDateStruct) => false;

  markDisabledDates() {
    this.markDisabled = (date: NgbDateStruct) => {
      if (this.inputForm.controls['date'].value) {
        let selectedDate = new Date(this.inputForm.controls['date'].value);
        let struct = {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate()
        };
        if ((date as NgbDate).equals(struct)) {
          return false;
        }
      }
      if ((date as NgbDate).after(this.maxCalendarDate)) return true;
      return (date as NgbDate).before(this.minCalendarDate);
    };
  }

  dropdownClosed(open: boolean) {
    this.inputForm.markAsTouched();
    this.dropdownOpen = open;
  }

  initCalendar() {
    this.navigationDate = this.getCalendarStartDate();
    this.calendarState = CalendarState.DAY;
  }

  getStringValue(): string {
    const date: Date | undefined = this.inputForm.controls['date'].value;
    const formTime: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
    const lang = this.translocoService.getActiveLang() === 'en' ? 'en-GB' : this.translocoService.getActiveLang();
    if (date && formTime && this.displayTime) {
      date.setHours(formTime.hour);
      date.setMinutes(formTime.minute);
      return date?.toLocaleString([lang], {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (date) {
      return date?.toLocaleString([lang], {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
      });
    } else return '';
  }

  getCalendarStartDate(): NgbDate {
    let navigationDate = this.currentDate;
    if (this.minDate && this.minDate.getTime() > navigationDate.getTime()) {
      navigationDate = this.minDate;
    }
    if (this.maxDate && this.maxDate.getTime() < navigationDate.getTime()) {
      navigationDate = this.maxDate;
    }
    return this.getNgbDate(this.inputForm.controls['date']?.value ?? navigationDate);
  }


  hasErrors() {
    return this.inputForm.errors || this.control.errors;
  }

  get maxCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.getNgbDate(this.maxDate ?? new Date(date.setFullYear(date.getFullYear() + 10)));
  }

  get minCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.getNgbDate(this.minDate ?? new Date(date.setFullYear(date.getFullYear() - 100)));
  }

  getNgbDate(date?: Date): NgbDate {
    date = new Date(date ?? this.currentDate);
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  getTime(date: Date | undefined, time: NgbTimeStruct | undefined): Date | undefined {
    let returnDate: Date | undefined;
    if (date) {
      returnDate = new Date(date);
    }
    if (returnDate && time) {
      returnDate.setHours(time.hour);
      returnDate.setMinutes(time.minute);
    }
    return date ? returnDate : undefined;
  }

  isTouched(): boolean {
    return this.inputForm.touched || this.control.touched
  }

}

enum CalendarState {
  DAY,
  MONTH,
  YEAR
}

class NavigationObject {
  constructor(public value: number, public disabled: boolean) {
  }
}
